// React & Hooks
import {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";

// Utils
import apiClient from "@/utils/global/apiClient";
import { isAxiosError } from "axios";

// Types
import { AuthContextType, UserDetails } from "@/types/index";

// Components
import AuthSpinner from "@/components/common/auth-spinner";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // we still use localStorage to avoid flicker, but truth comes from /users/me
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const storedAuth = localStorage.getItem("isAuthenticated");
    return storedAuth === "true";
  });
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(() => !isAuthenticated);

  const fetchUserDetails = useCallback(async () => {
    try {
      const response = await apiClient.get("/users/me");

      setIsAuthenticated(true);
      const superuser = response.data.is_superuser ?? false;
      setIsSuperuser(superuser);

      const user: UserDetails = {
        id: response.data.id,
        username: response.data.email, // use email as username
        isSuperuser: superuser,
      };
      setUserDetails(user);

      localStorage.setItem("isAuthenticated", "true");
    } catch (error) {
      // Not authenticated or token invalid
      setIsAuthenticated(false);
      setIsSuperuser(false);
      setUserDetails(null);
      localStorage.setItem("isAuthenticated", "false");
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // we think user is auth from localStorage → verify via /users/me
      fetchUserDetails();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchUserDetails]);

  const login = async (
    username: string, // this is actually the email now
    password: string
  ): Promise<{ success: boolean; status_code: number }> => {
    try {
      if (isAuthenticated) {
        await logout();
      }

      // FastAPI Users login expects x-www-form-urlencoded:
      // username=<email>&password=<password>
      const formData = new URLSearchParams();
      formData.append("username", username);
      formData.append("password", password);

      const response = await apiClient.post("/auth/jwt/login", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      // Cookie is set by backend; now fetch user details
      await fetchUserDetails();
      console.info("Login successful");
      return { success: true, status_code: response.status };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        const { status } = error.response || {};
        console.error("Login failed:", error);
        return { success: false, status_code: status || 500 };
      }
      console.error("Network or unknown error:", error);
      return { success: false, status_code: 500 };
    }
  };

  const logout = async () => {
    try {
      // FastAPI Users logout
      await apiClient.post("/auth/jwt/logout");
      setIsAuthenticated(false);
      setIsSuperuser(false);
      setUserDetails(null);
      localStorage.setItem("isAuthenticated", "false");
      console.info("Logout successful");
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <AuthSpinner />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, login, logout, isSuperuser, userDetails }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
